// Entry-edge normaliser: turns an action row's bound commands and valid service
// modes into the catalog ids every consumer downstream already looks up.
//
// A command's identity is its ENUM, not the UUID of a Message row. The enum is
// the contract shared with LiftCloud and VTS, it lives in `flatbuffer_schemas`,
// and it is what actually travels on the wire (`usePostCommand` sends
// `command: <enum value>`). A Message-row UUID only identifies a row in one
// database — the same `Control.VIP` has a different UUID in every environment.
//
// So the API sends the enum with the row (`cmd` / `toggle_cmd`, `valid_services`
// / `toggle_valid_services`) and this module consumes it, rewriting messageId /
// toggleMessageId / validStates / toggleValidStates to the catalog ids for those
// enums, once, at the point actions enter the UI. Everything below — the cards,
// the six button components, the floor strip — keeps doing the lookup it already
// does, unchanged.
//
// What made this necessary: the message catalog is derived from the generated
// FlatBuffer enums (useSchemaMessages), so its ids read "CONTROL:21" /
// "SERVICE:4". Rows from the typed/binary path stored the Message row's UUID
// instead (ActionBankT.messageId is a UuidT), and no UUID can ever equal
// "CONTROL:21" — those actions resolved to no command and every button on them
// refused to send. The UuidT conversion was never the problem: there was
// nothing in the catalog to convert it into.
import {
  BankActionInterface,
  ActionMessageRef,
  ActionCommandOption,
} from "../../../interfaces/Bank.interface";
import {
  ALL_SCHEMA_MESSAGES,
  MSG_TYPE_CONTROL,
  MSG_TYPE_SERVICE,
} from "../../../customHooks/useSchemaMessages";
import { commandOptionEnum } from "../actionButtons/actionButtonUtils";

/**
 * A number, or null for every other value — `undefined` and `null` included.
 *
 * `0` survives, and must: `Control.CarCallLock` and `Service.Auto` are both 0,
 * so "unset" can only ever be expressed by absence, never by a 0 sentinel.
 */
const asEnum = (value: unknown): number | null => (typeof value === "number" ? value : null);

/** LEGACY. The enum a nested message ref carries, under either spelling. */
const refEnum = (ref: ActionMessageRef | null | undefined): number | null =>
  asEnum(ref?.enumId ?? ref?.enum_id);

/**
 * LEGACY. The category a nested message ref belongs to. A command is always a
 * Control, so an absent category means CONTROL rather than "unknown" — the flat
 * form of the payload does not carry one at all.
 */
const refType = (ref: ActionMessageRef | null | undefined): number =>
  asEnum(ref?.messageTypeId ?? ref?.message_type_id) ?? MSG_TYPE_CONTROL;

/**
 * The catalog id for a (category, enum) pair, or undefined when the schema
 * catalog has no such member — a value the generated enums do not declare.
 *
 * Looked up in ALL_SCHEMA_MESSAGES rather than built by string concatenation,
 * so the id is the catalog's own and a change to how it composes ids cannot
 * silently produce a key that matches nothing.
 */
export const catalogIdForEnum = (messageTypeId: number, enumValue: number): string | undefined =>
  ALL_SCHEMA_MESSAGES.find((m) => m.messageTypeId === messageTypeId && m.enumId === enumValue)?.id;

/**
 * The catalog id for an action's bound command.
 *
 * `cmd` wins when present — but ONLY once its category has been checked. This
 * comment used to read "it is typed Control, so its category is never in
 * doubt", and that was true while `cmd` came from a Control-only source. It
 * stopped being true when the server began projecting
 * `ActionBank.message_value`, which is the enum of whatever message is bound.
 * The catalog command option is read next — it carries the same enum as
 * `numeric`, and it is the only source a row saved without `cmd` has. The
 * nested/flat refs are the legacy path below that, and only they need a category
 * probe. Returns the id currently on the row when nothing resolves, so a row
 * holding only a UUID keeps failing loudly (see diagnoseMessageId) instead of
 * being silently rewritten to some other command.
 */
/**
 * May this row's command enum be read as a `Control` value?
 *
 * An enum value is meaningless without its category, and the LN2 categories
 * collide numerically: 29 is `Traffic::CarToLobby` AND `Control::DisableDoor`;
 * 27 is `Traffic::IndependentService` AND `Control::RunDown`. Resolving against
 * the wrong one does not fail — it SUCCEEDS and names a real, different
 * command. Two buttons bound to Traffic messages came out addressing
 * `Control::RunDown` and `Control::DisableDoor` for exactly this reason.
 *
 * 0/absent is `NONE`: the server did not say, which is true of every server
 * predating `ActionBank.message_content_type`. Refusing there would blank every
 * action on an older stack, so absence keeps the previous behaviour. The guard
 * bites only where the server actually answered, and answered something else.
 */
const categoryAllowsControl = (contentType: number | null | undefined): boolean =>
  contentType === null || contentType === undefined || contentType === 0
    ? true
    : contentType === MSG_TYPE_CONTROL;

const resolveId = (
  currentId: string,
  cmd: number | null | undefined,
  option: ActionCommandOption | null | undefined,
  ref: ActionMessageRef | null | undefined,
  flatEnum: number | null | undefined,
  contentType?: number | null,
): string => {
  const command = asEnum(cmd) ?? commandOptionEnum(option);

  if (command !== null) {
    // A command whose category is NOT Control resolves to nothing, rather than
    // to some other category's member of the same number. The button is then
    // left with no bound command, and its own guard refuses the press and says
    // so — the correct outcome for a button wired to something the controller
    // reports rather than accepts.
    if (!categoryAllowsControl(contentType)) return currentId;

    return catalogIdForEnum(MSG_TYPE_CONTROL, command) ?? currentId;
  }

  const legacy = refEnum(ref) ?? asEnum(flatEnum);
  if (legacy === null) return currentId;
  return catalogIdForEnum(refType(ref), legacy) ?? currentId;
};

/**
 * Catalog ids for a list of `Service` enum values, or null when the field is
 * absent — meaning the row predates the contract and its `validStates` stand.
 *
 * A PRESENT but empty array is authoritative and returns `[]`: "no restriction"
 * has to be expressible, otherwise clearing an action's valid states in the UI
 * could never reach the buttons.
 *
 * Values the Service enum does not declare are dropped rather than kept as
 * unresolvable ids: `resolveValidStateEnums` would drop them one step later
 * anyway, and dropping here keeps the popover from listing a blank.
 */
const resolveServiceIds = (services: unknown): string[] | null => {
  if (!Array.isArray(services)) return null;
  return services
    .map((value) => {
      const service = asEnum(value);
      return service === null ? undefined : catalogIdForEnum(MSG_TYPE_SERVICE, service);
    })
    .filter((id): id is string => id !== undefined);
};

/** True when two id lists are the same values in the same order. */
const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Rewrite an action's commands and valid service modes to catalog ids.
 *
 * Leaves the action untouched — the same object, not a copy — when nothing
 * needed changing, so rows that already hold catalog ids keep working and a row
 * carrying neither an enum nor a resolvable id stays exactly as the API sent it.
 */
export const normalizeActionMessageIds = (action: BankActionInterface): BankActionInterface => {
  const messageId = resolveId(
    action.messageId,
    action.cmd,
    action.command,
    action.message,
    action.messageEnumId ?? action.message_enum_id,
    action.messageContentType ?? action.message_content_type,
  );
  const toggleMessageId = resolveId(
    action.toggleMessageId,
    action.toggleCmd ?? action.toggle_cmd,
    action.toggleCommand ?? action.toggle_command,
    action.toggleMessage ?? action.toggle_message,
    action.toggleMessageEnumId ?? action.toggle_message_enum_id,
    action.toggleMessageContentType ?? action.toggle_message_content_type,
  );

  // The list the row arrived with, guaranteed to be an array. BankActionInterface
  // types these as required, but the payload is `any` at the boundary and a row
  // saved without them is real — comparing lengths against undefined would turn a
  // missing field into a crash on the monitoring page.
  const currentValidStates = Array.isArray(action.validStates) ? action.validStates : [];
  const currentToggleValidStates = Array.isArray(action.toggleValidStates)
    ? action.toggleValidStates
    : [];

  const validStates =
    resolveServiceIds(action.validServices ?? action.valid_services) ?? currentValidStates;
  const toggleValidStates =
    resolveServiceIds(action.toggleValidServices ?? action.toggle_valid_services) ??
    currentToggleValidStates;

  if (
    messageId === action.messageId &&
    toggleMessageId === action.toggleMessageId &&
    sameIds(validStates, currentValidStates) &&
    sameIds(toggleValidStates, currentToggleValidStates)
  ) {
    return action;
  }
  return { ...action, messageId, toggleMessageId, validStates, toggleValidStates };
};

/** The same, for a whole list. */
export const normalizeActionsMessageIds = (
  actions: BankActionInterface[] | null | undefined,
): BankActionInterface[] => (actions ?? []).map(normalizeActionMessageIds);
